const IS_DEV = process.env.NODE_ENV === "development";

/**
 * Funnel event names. Centralized so producers and the Clarity dashboard agree
 * on a single vocabulary. Demo events keep their original names so existing
 * call sites in useDemoFlow.js / ReportDetailView.js continue to work.
 */
export const FUNNEL_EVENTS = {
  LANDING: "funnel_landing",
  BUILDER_STEP: "funnel_builder_step",
  GENERATE_SUCCESS: "funnel_generate_success",
  SIGNUP_SHOWN: "funnel_signup_shown",
  SIGNUP_SUCCESS: "funnel_signup_success",
  CLAIM_SUCCESS: "funnel_claim_success",
  FIRST_REAL_MEETING: "funnel_first_real_meeting",
};

/**
 * Fire a single custom event to Microsoft Clarity, then tag the session with
 * each property so events can be filtered by property in the Clarity dashboard.
 *
 * Guarded so it never throws when Clarity isn't loaded (dev, ad-blockers, etc).
 * This mirrors the precedent in AgendaRevealSummary.js#trackGuestCTA.
 */
const fireClarityEvent = (eventName, properties = {}) => {
  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.log(`[Analytics] ${eventName}`, properties);
  }

  try {
    window.clarity?.("event", eventName);
    Object.entries(properties).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      window.clarity?.("set", `${eventName}_${key}`, String(value));
    });
  } catch {
    // Clarity may not be loaded (dev, ad-blockers) — telemetry is best-effort.
  }
};

/**
 * Track demo-related events.
 * Events:
 * - demo_started: User begins demo
 * - demo_step_reached: Each step transition (includes step, stepIndex, timeElapsedMs)
 * - demo_activity_played: Activity goes live (includes activityId, activityType)
 * - demo_results_viewed: User opens results (includes activityId, winningOption)
 * - demo_report_viewed: User clicks "View Full Report" (includes agendaId)
 * - demo_abandoned: User clicks "End Demo" (includes lastStep, timeElapsedMs)
 * - demo_completed: User reaches final CTA (includes totalTimeMs, stepsCompleted)
 */
export const trackDemoEvent = (eventName, properties = {}) => {
  fireClarityEvent(eventName, properties);
};

/**
 * Track top-of-funnel / conversion events (landing, builder steps, generate,
 * signup, claim, first real meeting). Use the FUNNEL_EVENTS constants.
 */
export const trackFunnelEvent = (eventName, properties = {}) => {
  fireClarityEvent(eventName, properties);
};
